import { cn } from '@/lib/utils';

/**
 * A small on/off switch that reads correctly in both themes.
 *
 * The thumb is laid out with flex + translate (not absolute positioning, which
 * has no anchor inside a bare button), and its colour is the INVERSE of the
 * track in each state: primary/primary-foreground when on, muted/
 * muted-foreground when off. In dark mode --primary is near-white, so a thumb
 * tied to --background would vanish against it.
 */
export function Switch({
  checked,
  onCheckedChange,
  label,
  ariaLabel,
  title,
  disabled = false,
  size = 'default',
  className,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  /** The words beside the track; empty for a track alone, named by ariaLabel. */
  label: string;
  /** The name read out, where the words shown are cut short to fit ("Collapse" for "Collapse seen"). */
  ariaLabel?: string;
  title?: string;
  disabled?: boolean;
  /** `sm`: a shorter track and tighter padding, for a line with little room (the image list's foot). */
  size?: 'default' | 'sm';
  /** Over the button's own classes: a page sets the words' size to its line's. */
  className?: string;
}) {
  // The LABEL and the toggle are one control. A <label> wrapping a plain
  // button is not associated with anything, so clicking the words did nothing
  // while looking clickable: an operator could believe they had turned review
  // on and upload under the old setting.
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel ?? label}
      title={title}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'inline-flex cursor-pointer items-center rounded-md py-0.5 text-xs transition-colors',
        size === 'sm' ? 'gap-1.5 px-0.5' : 'gap-2 px-1',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'text-foreground' : 'text-muted-foreground',
        className,
      )}
    >
      {label ? <span>{label}</span> : null}
      <span
        aria-hidden
        className={cn(
          'inline-flex shrink-0 items-center rounded-full border px-0.5 transition-colors',
          size === 'sm' ? 'h-4 w-7' : 'h-5 w-9',
          // The thumb is placed by flex justification, not a transform:
          // translate utilities did not take effect in this build.
          checked ? 'justify-end border-primary bg-primary' : 'justify-start border-input bg-muted',
        )}
      >
        <span
          className={cn(
            'rounded-full shadow-sm',
            size === 'sm' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5',
            checked ? 'bg-primary-foreground' : 'bg-muted-foreground',
          )}
        />
      </span>
    </button>
  );
}
